import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import TruncatedText from 'src/components/TruncatedText';
import LoadingRows from 'src/components/Table/LoadingRows';
import NoDataCell from 'src/components/NoDataCell';
import TableCard from 'src/components/Table/TableCard';
import { PORTAL_ROUTE } from 'src/constants/routes';
import { useInfinitePolicyVersionDevices } from 'src/hooks/environmentWdacPolicy';
import { DEVICES_PREVIOUS_PATH } from 'src/pages/devices/deviceDetails/constant';
import { formatDate } from 'src/utils/formatTime';
import { getWdacStatusLabel } from 'src/utils/enums/wdacStatus';

const HEADER_SX = { fontSize: '0.7rem', fontWeight: 600, py: 0.75 };
const CELL_SX = { fontSize: '0.7rem', py: 0.75, lineHeight: 1.3 };
const ROW_SX = { cursor: 'pointer', '&:hover': { backgroundColor: '#565656' } };

const COLUMNS = [
  { key: 'deviceName', label: 'Device Name', width: '46%' },
  { key: 'wdacDeviceStatus', label: 'WDAC Status', width: '22%' },
  { key: 'lastAppliedDate', label: 'Last Applied', width: '32%' },
];

// The devices that have reported one version of this policy, most recently reported first.
const PolicyVersionDevicesModal = ({ open, version, onClose }) => {
  const { policyId } = useParams();
  const navigate = useNavigate();

  const devicesQuery = useInfinitePolicyVersionDevices({
    envPolicyId: policyId,
    versionId: version?.id,
    enabled: open,
  });

  const devices = useMemo(
    () => devicesQuery.data?.pages?.flatMap(page => page.results || []) || [],
    [devicesQuery.data],
  );

  const [sentryRef] = useInfiniteScroll({
    loading: devicesQuery.isFetchingNextPage,
    hasNextPage: !!devicesQuery.hasNextPage,
    onLoadMore: devicesQuery.fetchNextPage,
    rootMargin: '0px 0px 200px 0px',
  });

  const openDevice = device => {
    sessionStorage.setItem(
      DEVICES_PREVIOUS_PATH,
      `${window.location.pathname}${window.location.search}`,
    );
    navigate(`${PORTAL_ROUTE}/devices/${device.deviceId}/general`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { backgroundColor: 'background.dialog' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <Iconify icon="mdi:monitor-multiple" width={22} height={22} color="#87C8E0" />
        Devices on Version {version?.policyVersion}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12, color: '#CFCFCF' }}>
          <Iconify icon="mdi:close" width={20} height={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack gap={1.5}>
          <Typography variant="body2" sx={{ color: '#ccc' }}>
            {version?.policyName ? `${version.policyName}. ` : ''}Devices that have reported this
            version at any point, most recent first, whether or not it is still active on them; a
            device that has since moved to another version is listed under both. Archived and
            revoked devices are excluded.
          </Typography>

          <TableCard title="Devices" maxHeight={420} edgePadding={2}>
            <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead sx={{ backgroundColor: '#3f3f3f' }}>
                <TableRow>
                  {COLUMNS.map(column => (
                    <TableCell key={column.key} sx={{ ...HEADER_SX, width: column.width }}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
                {devicesQuery.isLoading ? (
                  <LoadingRows columnCount={COLUMNS.length} rowCount={5} />
                ) : (
                  <>
                    {devices.map((device, index) => (
                      <TableRow
                        key={device.deviceId}
                        ref={index === devices.length - 1 ? sentryRef : null}
                        onClick={() => openDevice(device)}
                        sx={ROW_SX}>
                        <TableCell sx={CELL_SX}>
                          <TruncatedText value={device.deviceName || '—'} bold />
                        </TableCell>
                        <TableCell sx={CELL_SX}>
                          <TruncatedText
                            value={getWdacStatusLabel(device.wdacDeviceStatus)}
                            muted
                          />
                        </TableCell>
                        <TableCell sx={CELL_SX}>
                          <TruncatedText value={formatDate(device.lastAppliedDate)} muted />
                        </TableCell>
                      </TableRow>
                    ))}
                    {devicesQuery.isFetchingNextPage && (
                      <LoadingRows columnCount={COLUMNS.length} rowCount={2} />
                    )}
                    {!devicesQuery.isFetching && !devices.length && (
                      <NoDataCell colSpan={COLUMNS.length}>
                        No devices have reported this version.
                      </NoDataCell>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </TableCard>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default PolicyVersionDevicesModal;
